import LocalizedStrings from 'react-localization';
import { withCommon } from '../../services/translation/commonTranslations';

const adminStrings = new LocalizedStrings({
  en: {
    menu: {
      general: 'General',
      locations: 'Locations',
      leaveTypes: 'Leave Types',
      contracts: 'Contracts',
      skills: 'Skills',
      tasks: 'Tasks'
    },
    generalSection: {
      title: 'General Settings',
      defaultShiftTimes: 'Default Shift Times',
      defaultShiftTimesDescription:
        'This settings controls the default start and end of a shift when it is dragged onto a roster.',
      defaultShiftTimesDescriptionPart2: 'This can be overridden on a tenant or location level.',
      startTime: 'Start Time',
      endTime: 'End Time'
    },
    locationsSection: {
      title: 'Locations',
      table: {
        id: 'Id',
        name: 'Name',
        edit: 'Edit',
        addNew: 'Add New',
        disabled: 'Disabled'
      },
      editLocationTimes: {
        defaultTimes: 'Default Times',
        title: (locationName: string) => `Default Times: ${locationName}`,
        startTime: 'Start Time',
        endTime: 'End Time',
        shiftTimes: {
          defaultShiftTimes: 'Default Shift Times',
          defaultShiftTimesDescription:
            'This setting controls the default start and end of a shift when it is dragged onto a roster.'
        },
        rosterTimes: {
          minTime: 'Minimum Time',
          maxTime: 'Maximum Time',
          defaultRosterTimes: 'Default Roster Times',
          defaultRosterTimesDescription:
            'This setting controls the default minimum and maximum times that shifts can be scheduled on a roster.',
          defaultRosterTimesDescriptionPart2: 'These values can be edited for individual rosters.',
          maxCannotBeLowerThanMin: 'Maximum time cannot be lower than the minimum time'
        },
        openingTimes: {
          defaultOpeningTimes: 'Default Opening Times',
          defaultOpeningTimesDescription:
            'This setting controls the default opening and closing times for the roster. Staff can still be rostered outside of these times, they are shown as a visual indicator only.',
          defaultOpeningTimesDescriptionPart2: 'These values can be edited for individual rosters.'
        }
      },
      deactivateReactivate: {
        deactivate: (name: string) => `Deactivate Location: ${name}`,
        reactivate: (name: string) => `Reactivate Location: ${name}`,
        deactivateInfo:
          "If you deactivate this location, it won't be available for future use. Existing assignments will remain unaffected.",
        reactivateInfo: 'If you reactivate this location, it will become available for use again.',
        successDeactivate: 'Location deactivated',
        successReactivate: 'Location reactivated',
        errorDeactivate: 'Error deactivating location',
        errorReactivate: 'Error reactivating location',
        confirmTitle: (name: string) => `Deactivate ${name}?`,
        confirmReactivateTitle: (name: string) => `Reactivate ${name}?`,
        confirmDeactivateBody: 'This will make the location unavailable for future use.',
        confirmReactivateBody: 'This will make the location available for use again.'
      },
      editLocation: {
        locations: 'Locations',
        locationDetails: 'Location Details',
        title: (locationName: string) => `Location Details: ${locationName}`,
        name: 'Name',
        externalId: 'External ID',
        division: 'Division',
        holidayRegion: 'Holiday Region',
        locationName: 'Location Name',
        divisionRequired: 'Division is required',
        uniqueName: 'Location name must be unique.',
        uniqueId: 'Location external ID must be unique.',
        phoneNumber: 'Phone Number',
        address: 'Address',
        successMessage: (name: string) => `Location ${name} updated successfully.`,
        errorMessage: (name: string) => `Unable to update location ${name}.`
      },
      addDialog: {
        title: 'Add New Location',
        newLocation: 'New Location Name',
        locationName: 'Location Name',
        division: 'Division',
        divisionRequired: 'Division is required',
        holidayRegion: 'Holiday Region',
        description: 'Select the public holiday region used for this location.',
        inheritOption: 'Use tenant default',
        nameRequired: 'Location name required',
        uniqueName: 'Location name must be unique.',
        divisionPlaceholder: 'Select a division'
      }
    },
    skillsSection: {
      title: 'Skills',
      table: {
        id: 'Id',
        name: 'Name',
        edit: 'Edit',
        addNew: 'Add New',
        disabled: 'DISABLED'
      },
      editSkill: {
        skill: 'Skill',
        skillDetails: 'Skill Details',
        displayName: 'Display Name',
        title: (skillName: string) => `Skill Details: ${skillName}`,
        name: 'Name',
        skillName: 'Skill Name',
        uniqueName: 'Skill name must be unique.',
        successMessage: (name: string) => `Skill ${name} updated successfully.`,
        errorMessage: (name: string) => `Unable to update skill ${name}.`
      },
      deactivateReactivate: {
        deactivate: (name: string) => `Deactivate Skill: ${name}`,
        reactivate: (name: string) => `Reactivate Skill: ${name}`,
        deactivateInfo:
          "If you deactivate this skill, you won't be able to assign staff members this skill. Any staff assigned to it will remain unaffected.",
        reactivateInfo: 'If you reactivate this skill, you will be able to assign staff members this skill.',
        successDeactivate: 'Skill deactivated',
        successReactivate: 'Skill reactivated',
        errorDeactivate: 'Error deactivating skill',
        errorReactivate: 'Error reactivating skill',
        confirmTitle: (name: string) => `Deactivate ${name}?`,
        confirmReactivateTitle: (name: string) => `Reactivate ${name}?`,
        confirmDeactivateBody: 'This will prevent the skill from being assigned to staff.',
        confirmReactivateBody: 'This will make the skill available to assign to staff again.'
      },
      addDialog: {
        title: 'Add New Skill',
        newSkill: 'New Skill Name',
        skillName: 'Skill Name',
        nameRequired: 'Skill name required',
        uniqueName: 'Skill name must be unique.'
      }
    },
    tasksSection: {
      title: 'Tasks',
      table: {
        id: 'Id',
        name: 'Task Name',
        icon: 'Icon',
        skillsNeeded: 'Skills Needed',
        edit: 'Edit',
        addNew: 'Add New',
        disabled: 'DISABLED'
      },
      edit: {
        task: 'Task',
        taskDetails: 'Task Details',
        title: (taskName: string) => `Task Details: ${taskName}`,
        name: 'Name',
        taskName: 'Task Name',
        taskDisplayName: 'Display Name',
        uniqueName: 'Task name must be unique.',
        successMessage: (name: string) => `Task ${name} updated successfully.`,
        errorMessage: (name: string) => `Unable to update task ${name}.`,
        textColour: 'Icon Colour',
        backgroundColour: 'Background Colour',
        taskIcon: 'Task Icon',
        requiredSkills: 'Required Skills',
        requiredSkillsDescription:
          'If this task is assigned to a shift, then only users with all of the checked skills can be assigned to that shift.',
        preview: 'Preview'
      },
      deactivateReactivate: {
        deactivate: (name: string) => `Deactivate Task: ${name}`,
        reactivate: (name: string) => `Reactivate Task: ${name}`,
        deactivateInfo:
          "If you deactivate this task, it won't be available for future use. Existing assignments will remain unaffected.",
        reactivateInfo: 'If you reactivate this task, it will become available for use again.',
        successDeactivate: 'Task deactivated',
        successReactivate: 'Task reactivated',
        errorDeactivate: 'Error deactivating task',
        errorReactivate: 'Error reactivating task',
        confirmTitle: (name: string) => `Deactivate ${name}?`,
        confirmReactivateTitle: (name: string) => `Reactivate ${name}?`,
        confirmDeactivateBody: 'This will make the task unavailable for future use.',
        confirmReactivateBody: 'This will make the task available for use again.'
      },
      addDialog: {
        title: 'Add New Task',
        newTask: 'New Task Name',
        taskName: 'Task Name',
        nameRequired: 'Task name required',
        uniqueName: 'Task name must be unique.'
      }
    },
    contractsSection: {
      title: 'Contracts',
      table: {
        id: 'Id',
        name: 'Name',
        contractCode: 'Contract Code',
        status: 'Status',
        displayOrder: 'Display Order',
        maxHoursPerWeek: 'Max Hours Per Week',
        maxHoursPerDay: 'Max Hours Per Day',
        maxHoursPerShift: 'Max Hours Per Shift',
        earliestStartTime: 'Earliest Start',
        latestEndTime: 'Latest End',
        edit: 'Edit',
        addNew: 'Add New',
        disabled: 'DISABLED',
        showColumns: 'Show Columns',
        columns: 'Columns',
        clearAll: 'Clear all',
        active: 'Active',
        inactive: 'Inactive'
      },
      edit: {
        contract: 'Contract',
        contractDetails: 'Contract Details',
        title: (contractName: string) => `Contract Details: ${contractName}`,
        name: 'Name',
        contractName: 'Contract Name',
        uniqueName: 'Contract name must be unique.',
        successMessage: (name: string) => `Contract ${name} updated successfully.`,
        errorMessage: (name: string) => `Unable to update contract ${name}.`,
        earliestStartTime: 'Earliest Start Time',
        latestEndTime: 'Latest End Time',
        minHoursPerWeek: 'Minimum Hours Per Week',
        maxHoursPerWeek: 'Maximum Hours Per Week',
        minHoursPerDay: 'Minimum Hours Per Day',
        maxHoursPerDay: 'Maximum Hours Per Day',
        maxDaysPerWeek: 'Maximum Days Per Week',
        maxConsecutiveDays: 'Maximum Consecutive Days',
        maxHoursPerShift: 'Maximum Hours Per Shift',
        allowOvertime: 'Allow Overtime',
        notEnforcedTooltip: 'Set to 0 to leave this limit unenforced',
        notEnforcedTimeTooltip: 'Set to 00:00 to leave this limit unenforced',
        maxCannotBeLowerThanMin: 'Maximum time cannot be lower than the minimum time'
      },
      deactivateReactivate: {
        deactivate: (name: string) => `Deactivate Contract: ${name}`,
        reactivate: (name: string) => `Reactivate Contract: ${name}`,
        deactivateInfo:
          "If you deactivate this contract, you won't be able to assign staff members this contract. Any staff assigned to it will remain unaffected.",
        reactivateInfo: 'If you reactivate this contract, you will be able to assign staff members this contract.',
        successDeactivate: 'Contract deactivated',
        successReactivate: 'Contract reactivated',
        errorDeactivate: 'Error deactivating contract',
        errorReactivate: 'Error reactivating contract',
        confirmTitle: (name: string) => `Deactivate ${name}?`,
        confirmReactivateTitle: (name: string) => `Reactivate ${name}?`,
        confirmDeactivateBody: 'This will prevent the contract from being assigned to staff.',
        confirmReactivateBody: 'This will make the contract available to assign to staff again.'
      },
      addDialog: {
        title: 'Add New Contract',
        newContract: 'New Contract Name',
        contractName: 'Contract Name',
        nameRequired: 'Contract name required',
        uniqueName: 'Contract name must be unique.',
        baseContract: 'Base Contract',
        baseContractDescription:
          "Selecting a base contract will copy over all data from that contract. If you leave this blank, you'll create a fresh contract with no data.",
        placeholder: 'Select'
      }
    },
    page: {
      admin: 'Admin',
      title: 'Admin Settings',
      locationTitle: 'Location Settings'
    },
    state: {
      advancedLeaveDisabled: 'Advanced leave is not enabled for this organisation.',
      loadingLeaveTypes: 'Loading leave types...'
    },
    leaveTypeSection: {
      title: 'Leave Types',
      addDialog: {
        title: 'Add New Leave Type',
        newLeaveType: 'New Leave Type',
        leaveTypeName: 'Leave Type Name',
        nameRequired: 'Leave Type name required',
        uniqueName: 'Leave Type name must be unique.'
      },
      table: {
        name: 'Name',
        displayName: 'Display Name',
        color: 'Color',
        managerOnly: 'Manager Only',
        edit: 'Edit',
        delete: 'Delete',
        addNew: 'Add New',
        disabled: 'DISABLED'
      },
      deleteLeaveTypeDialog: {
        title: (name: string) => `Delete Leave Type: ${name}`,
        confirm: 'Continue',
        message: 'Are you sure you want to delete this leave type?',
        futureMessage:
          'This leave type is still associated to future leave requests, it cannot be removed until the requests are allocated to another leave type.',
        pastMessage:
          "There are historical leave requests using this leave type. The leave type will be replaced to 'unknown' in the historical record.",
        loading: 'Checking leave requests using this leave type...',
        validationError: 'Unable to validate leave type usage.',
        deleteSuccess: 'Leave type deleted.',
        deleteError: 'Unable to delete leave type.',
        futureListHeading: 'Future leave requests',
        unknownEmployee: 'Unknown employee'
      },
      deactivateReactivate: {
        deactivate: (name: string) => `Deactivate Leave Type: ${name}`,
        reactivate: (name: string) => `Reactivate Leave Type: ${name}`,
        deactivateInfo:
          'If you deactivate the leave type, users will not be able to create new leave requests of this type. Any existing requests will remain in the system.',
        reactivateInfo:
          'If you reactivate the leave type, users will be able to create new leave requests of this type again.',
        successDeactivate: 'Leave Type deactivated',
        successReactivate: 'Leave Type reactivated',
        errorDeactivate: 'Error deactivating leave type',
        errorReactivate: 'Error reactivating leave type',
        confirmTitle: (name: string) => `Deactivate ${name}?`,
        confirmReactivateTitle: (name: string) => `Reactivate ${name}?`,
        confirmDeactivateBody: 'This will make the leave type unavailable for future use.',
        confirmReactivateBody: 'This will make the leave type available for use again.'
      },
      edit: {
        leaveType: 'Leave Type',
        leaveTypeDetails: 'Leave Type Details',
        title: (name: string) => `Edit Leave Type: ${name}`,
        name: 'Name',
        displayName: 'Display Name',
        displayNameHelp: 'This will be displayed in the UI',
        code: 'Code',
        backgroundColor: 'Background Color',
        textColor: 'Text Colour',
        textColorBlack: 'Black',
        textColorWhite: 'White',
        managerOnly: 'Manager Only',
        managerOnlyDescription: 'Only managers can assign this leave type',
        activationTitle: 'Leave Type Status',
        discardTitle: 'Discard changes?',
        discardMessage: 'You have unsaved changes. If you cancel now, they will be lost.',
        discardConfirm: 'Discard',
        cancel: 'Cancel',
        save: 'Save',
        preview: 'Preview',
        successMessage: (name: string) => `Leave Type ${name} updated successfully.`,
        errorMessage: (name: string) => `Unable to update Leave Type ${name}.`
      }
    }
  }
});

export default withCommon<typeof adminStrings>(adminStrings);
